import type { BlogPost } from './types'

export const post: BlogPost = {
  slug: 'travel-aware-availability',
  title: 'Travel-Aware Availability: The Missing Layer in Online Booking',
  excerpt:
    'A mobile appointment is only available if the operator can realistically get there. Here is how travel-aware availability changes the booking experience.',
  content: `
Most scheduling tools treat availability as a grid of time. Mobile businesses need a grid of time plus distance.

If a customer books a slot that looks open but cannot fit the route, the operator has to repair the schedule manually. That repair usually happens after the customer already trusted the booking page.

## Availability is a promise

When a customer sees a time on a booking page, they read it as a promise. For a mobile provider, that promise depends on more than open hours.

It depends on:

- The customer's service address.
- Existing confirmed appointments.
- Imported calendar busy time.
- The operator's start and end locations.
- Service duration and buffers.
- Drive time between stops.

Ignoring any of those inputs makes the promise weaker.

## Why big buffers are not enough

Many solo operators solve travel uncertainty by adding large buffers. That prevents some bad bookings, but it also hides good ones.

A 45-minute buffer may be too short for a cross-town job and too long for a neighbor nearby. Static buffers flatten the day. Travel-aware availability adapts the day to the actual customer location.

## How Zippity approaches it

Zippity first confirms that the customer address is inside the operator's service area. Then it looks for slots that can support the selected service and the travel pattern around that appointment.

When precise travel lookup is available, it uses that. When a provider is unavailable, the system can fall back to cached or estimated behavior rather than making the customer guess.

## The operator benefit

Travel-aware availability gives solo operators a booking page that behaves more like an assistant:

- It protects impossible times.
- It reduces back-and-forth rescheduling.
- It keeps the calendar useful instead of overblocked.
- It lets customers book without waiting for a manual route check.

For mobile services, that is the difference between a calendar and a real booking system.
  `,
  author: {
    name: 'Zippity',
  },
  date: '2026-04-25',
  readTime: '4 min read',
  tags: ['travel-time', 'availability', 'routing', 'mobile-services'],
}
